// Constants for the Create Book wizard

import { WizardStep } from './types';

// Step definitions with labels and order
// Note: story-selection steps removed - templates are now optional within story-creation
export const WIZARD_STEPS: {
  single: WizardStep[];
  multi: WizardStep[];
} = {
  single: [
    'story-type',
    'photo-upload',
    'character-details-review',  // Combined step: name, age, and AI appearance review
    'style',
    'story-creation',  // Now includes optional story template selection
    'story-preview'
  ],
  multi: [
    'story-type',
    'photo-upload',
    'multi-character-details',
    'multi-character-appearance',
    'style',
    'story-creation',  // Now includes optional story template selection
    'story-preview'
  ]
};

// Step labels for display
export const STEP_LABELS: Record<WizardStep, string> = {
  'story-type': 'Story Type',
  'photo-upload': 'Upload Photo',
  'character-details': 'Character',
  'character-details-review': 'Character Details',  // New combined step
  'multi-character-details': 'Characters',
  'appearance': 'Appearance',
  'appearance-review': 'Review',
  'story-selection': 'Choose Story',  // Kept for backwards compatibility
  'multi-story-selection': 'Choose Story',  // Kept for backwards compatibility
  'multi-character-appearance': 'Appearance',
  'style': 'Style',
  'story-creation': 'Your Story',  // Updated: now includes optional template selection
  'story-preview': 'Preview'
};

// Step descriptions for help tooltips
export const STEP_DESCRIPTIONS: Record<WizardStep, string> = {
  'story-type': 'Choose between a single character or multiple characters story',
  'photo-upload': 'Upload a clear photo of the character(s)',
  'character-details': 'Tell us about your character',
  'character-details-review': 'Enter details and review detected features',  // New combined step
  'multi-character-details': 'Tell us about each character',
  'appearance': 'Customize how your character looks',
  'appearance-review': 'Review and adjust detected appearance',
  'story-selection': 'Choose a story template for your book',  // Kept for backwards compatibility
  'multi-story-selection': 'Select a story template for your multi-character adventure',  // Kept for backwards compatibility
  'multi-character-appearance': 'Customize how each character looks',
  'style': 'Select the visual style for your book',
  'story-creation': 'Choose a template or create your own story',  // Updated: now includes optional template selection
  'story-preview': 'Review and finalize your story'
};

// Default values for hidden fields
export const WIZARD_DEFAULTS = {
  pageCount: 16, // Changed from 12 to 16 as the default (recommended option)
  culturalContext: '',
  processingPriority: 'standard',
  relationshipType: 'siblings' // for multi-character
};

// Validation messages
export const VALIDATION_MESSAGES = {
  required: 'This field is required',
  photoRequired: 'Please upload a photo',
  nameRequired: 'Please enter a name',
  ageRequired: 'Please select an age',
  genderRequired: 'Please select a gender',
  styleRequired: 'Please select a style',
  titleRequired: 'Please enter a story title'
};

// Animation durations (in ms)
export const ANIMATION_DURATION = {
  stepTransition: 300,
  elementFade: 200,
  buttonHover: 150
};

// Mobile breakpoint
export const MOBILE_BREAKPOINT = 768;

// Touch gesture thresholds
export const SWIPE_THRESHOLD = 50; // pixels
export const SWIPE_VELOCITY_THRESHOLD = 0.3; // pixels per ms

/** Steps that exist for routing but are never shown (the wizard auto-skips 'story-type'). */
export const HIDDEN_STEPS: ReadonlySet<WizardStep> = new Set<WizardStep>(['story-type']);

/** The steps a visitor actually sees, in order. Use for every "Step N of M" display. */
export function visibleSteps(steps: readonly WizardStep[]): WizardStep[] {
  return steps.filter((s) => !HIDDEN_STEPS.has(s));
}

/** The visible step before `current`, or null when `current` is the first visible step. */
export function previousVisibleStep(steps: readonly WizardStep[], current: WizardStep): WizardStep | null {
  const i = steps.indexOf(current);
  for (let j = i - 1; j >= 0; j--) {
    if (!HIDDEN_STEPS.has(steps[j])) return steps[j];
  }
  return null;
}
